import React, { FC, MouseEventHandler } from 'react';
import { useSlideIcons } from '@/util/slideIcons.ts';
import { useNavigation } from '@/navigation/NavigationContex.ts';
import { ProgressBar } from './ProgressBar';
import { fromTemplate } from '@/translation/fromTemplate.tsx';
import { useUiEvent } from '@/util/useUiEvent.ts';
import { useLanguage } from '@/eventConfig/LanguageContext.tsx';
import { ConfigMetaDefaultLocales } from '@/eventConfig/types.ts';
import translationConfig from '@/eventConfig/configurations/translation-config.yaml';
import 'flag-icons/css/flag-icons.min.css';

interface HeaderProps {
  showSidePanel: () => void;
  templateData: any;
}

export const Header: FC<HeaderProps> = ({ showSidePanel, templateData }) => {
  const { currentSlide, navControl } = useNavigation();
  const { cancelIcon, infoIcon, cancelLabel, infoLabel } = useSlideIcons(currentSlide!);
  const { handleUiEvent } = useUiEvent();

  const { hasInfo, hasCancel } = navControl;

  const showProgressBar = currentSlide && currentSlide.id !== 'home';
  const showLanguageSelect = currentSlide && currentSlide.id === 'home';

  const greeting = templateData.form?.firstname?.value ? fromTemplate(templateData.meta.login, templateData) : '';

  const handleCancel: MouseEventHandler = (_e) => {
    handleUiEvent({
      type: 'nav',
      target: 'cancel',
    });

    /* overrides cancel layer content if in the slide config the layer object 'overrideCancel' is provided and configured */
    if (currentSlide?.overrideCancel) {
      handleUiEvent({
        type: 'layer',
        text: currentSlide.overrideCancel.text,
        title: currentSlide.overrideCancel.title,
        buttons: currentSlide.overrideCancel.buttons,
        icon: currentSlide.overrideCancel.icon ?? 'cancel',
      });
    } else {
      handleUiEvent({
        type: 'layer',
        ref: '@cancel',
      });
    }
  };

  const handleInfo: MouseEventHandler = (_e) => {
    handleUiEvent({
      type: 'layer',
      text: currentSlide?.info,
      title: currentSlide?.infoLabel,
    });
  };
  const { setLocale } = useLanguage();
  const { availableLanguages, fallbackLanguages } = translationConfig;
  const handleSetLanguage = (value: ConfigMetaDefaultLocales) => {
    handleUiEvent({
      type: 'layer',
      ref: '@switchLanguage',
    });
    setLocale(value);
  };

  return (
    <>
      {showProgressBar && (
        <div className="Progress">
          <ProgressBar />
        </div>
      )}
      <div className="Header">
        <span className="logo" onClick={showSidePanel}>
          DKMS
        </span>

        {hasCancel && (
          <button disabled={!hasCancel} className="nav-cancel" onClick={handleCancel}>
            <i className={cancelIcon} />
            {cancelLabel}
          </button>
        )}

        <div className="login-name">{greeting}</div>
        {showLanguageSelect && templateData.meta.locales?.length > 1 && (
          <ul className={'select-language'} role="list" aria-label="Select language">
            {templateData.meta.locales.map((value: ConfigMetaDefaultLocales) => {
              const languageFlag =
                templateData.meta.country in availableLanguages && availableLanguages[templateData.meta.country][value]
                  ? availableLanguages[templateData.meta.country][value]
                  : fallbackLanguages[value];
              return (
                <li
                  key={`lang_${value}`}
                  value={value}
                  className={`fi fi-${languageFlag.toLowerCase()}`}
                  role="button"
                  tabIndex={0}
                  aria-label={`Switch language to ${value}`}
                  onClick={() => handleSetLanguage(value)}
                  onKeyDown={(e) => {
                    const key = e.key;
                    if (key === ' ' || key === 'Spacebar' || key === 'Enter' || key === 'NumpadEnter') {
                      e.preventDefault();
                      handleSetLanguage(value);
                      return;
                    }
                    if (key === 'ArrowRight' || key === 'ArrowLeft' || key === 'ArrowUp' || key === 'ArrowDown') {
                      e.preventDefault();
                      const current = e.currentTarget as HTMLLIElement;
                      const list = current.parentElement;
                      if (!list) return;
                      const items = Array.from(list.querySelectorAll('li[role="button"]')) as HTMLLIElement[];
                      const idx = items.indexOf(current);
                      if (idx === -1) return;
                      const dir = key === 'ArrowRight' ? 1 : -1;
                      const nextIdx = (idx + dir + items.length) % items.length;
                      items[nextIdx]?.focus();
                    }
                  }}
                />
              );
            })}
          </ul>
        )}
        {hasInfo && (
          <button disabled={!hasInfo} className="nav-info" onClick={handleInfo}>
            <i className={infoIcon} />
            <span className="d-none d-sm-inline">{infoLabel}</span>
          </button>
        )}
      </div>
    </>
  );
};
